import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from '../ui/Header';
import { useSubscription } from '../../hooks/useSubscription';
import { PanelLeft, PanelLeftClose, Menu, X, AlertTriangle } from 'lucide-react';

const Layout = ({ children }) => {
  const { subscription, isPastDue, hasActiveSubscription } = useSubscription();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);


  // Close mobile menu when screen is resized to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="mobile-nav-overlay animate-fade-in"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Toggle Button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="fixed top-4 left-4 lg:hidden p-2 bg-white dark:bg-[#111] border border-gray-200 dark:border-white/[0.06] rounded-[10px] shadow-md hover:shadow-lg transition-all"
        style={{ zIndex: 9999 }}
        aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
      >
        {isMobileMenuOpen ? (
          <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        ) : (
          <Menu className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        )}
      </button>

      {/* Desktop Toggle Button - hidden on mobile */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="hidden lg:flex fixed w-6 h-6 bg-white dark:bg-[#111] border border-gray-200 dark:border-white/[0.06] rounded-full items-center justify-center shadow-lg hover:shadow-xl"
        style={{
          left: isCollapsed ? '44px' : '244px',
          top: '24px',
          zIndex: 9999,
          transition: isCollapsed
            ? 'left 350ms ease-in-out 350ms, box-shadow 200ms ease-in-out'
            : 'left 350ms ease-in-out, box-shadow 200ms ease-in-out'
        }}
      >
        {isCollapsed ? (
          <PanelLeft className="w-3 h-3 text-gray-600 dark:text-gray-400" />
        ) : (
          <PanelLeftClose className="w-3 h-3 text-gray-600 dark:text-gray-400" />
        )}
      </button>

      <div className="flex min-h-screen lg:h-screen">
        <Sidebar
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-[#0a0a0a]">
          <Header />

          {/* Past Due Banner */}
          {isPastDue() && (
            <div className="bg-red-500/[0.05] dark:bg-red-500/[0.05] border-b border-red-500/20 px-4 sm:px-6 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-300">
                    Your last payment failed. Please update your payment method to avoid service interruption.
                  </p>
                </div>
                <Link
                  to="/dashboard/billing"
                  className="text-sm font-medium text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100 whitespace-nowrap underline"
                >
                  Update billing
                </Link>
              </div>
            </div>
          )}

          {/* No Subscription Banner - disabled, Dashboard overview handles this */}
          {/* {!hasActiveSubscription() && !isPastDue() && (
            <div className="bg-orange-500/[0.05] dark:bg-orange-500/[0.05] border-b border-orange-500/20 px-4 sm:px-6 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <p className="text-sm text-orange-700 dark:text-orange-300">
                    You don't have an active subscription. Subscribe to a plan to access all features.
                  </p>
                </div>
                <Link
                  to="/dashboard/billing"
                  className="text-sm font-medium text-orange-700 dark:text-orange-300 hover:text-orange-900 dark:hover:text-orange-100 whitespace-nowrap underline"
                >
                  View plans
                </Link>
              </div>
            </div>
          )} */}

          {/* Main Content */}
          <main className="flex-1 lg:overflow-auto">
            {children}
          </main>
        </div>
      </div>
    </>
  );
};

export default Layout;